import { cx } from 'cva';

import { DottedBorderSide } from '@components/ui/DottedBorder';
import { Checkmark } from '@components/ui/Checkmark';
import { Badge } from '@components/ui/Badge';

import type { PlanData } from './content';
import { InfoTip } from '@components/pricing/InfoTip';

interface PlanTableProps {
  data: Record<string, PlanData>;
  title: string;
  customTableHeader?:
    | 'Pro with Authentication add-on'
    | 'Pro with Administration add-on'
    | 'Pro with B2B SaaS add-on';
}

export function PlanTable({
  title,
  data,
  customTableHeader = 'Pro with Authentication add-on'
}: PlanTableProps) {
  let totalDataKeys = 0;

  Object.values(data).forEach(subObject => {
    let keysCount = 0;
    Object.keys(subObject).forEach(key => {
      if (key !== 'titleTooltip') {
        keysCount++;
      }
    });
    totalDataKeys = Math.max(totalDataKeys, keysCount);
  });

  return (
    <div className="mx-auto max-w-[76rem] space-y-20 bg-white md:px-8 xl:px-0">
      <table className="divide-y">
        <thead>
          <tr
            className={cx(
              '[&_th:first-child]:pb-6 [&_th:first-child]:pl-0 [&_th:first-child]:text-left',
              '[&_th]:py-6 [&_th]:text-center lg:[&_th]:w-[240px] xl:[&_th]:w-[304px]',
              '[&_th:last-child]:pr-0'
            )}
          >
            <th
              scope="col"
              className="relative text-[1.125rem] font-medium leading-7"
            >
              {title}
              <DottedBorderSide side="right" overhang={0} />
            </th>
            <th scope="col" className="relative">
              <div className="mx-auto w-5/6 space-y-2">
                <h4 className="text-[0.8125rem] font-semibold leading-6">
                  Free Plan
                </h4>
              </div>
              <DottedBorderSide side="right" overhang={0} />
            </th>

            <th scope="col" className="relative">
              <div className="mx-auto w-5/6 space-y-2">
                <h4 className="text-[0.8125rem] font-semibold leading-6 text-[#00AEE3]">
                  Pro Plan
                </h4>
              </div>
              {totalDataKeys !== 2 && (
                <DottedBorderSide side="right" overhang={0} />
              )}
            </th>
            {totalDataKeys === 3 && (
              <th scope="col">
                <div className="mx-auto w-5/6 space-y-2">
                  {customTableHeader === 'Pro with Authentication add-on' && (
                    <>
                      <h4 className="inline-flex items-center gap-1 text-[0.8125rem] font-semibold leading-6">
                        <span className="text-newPurple-500">Pro</span>
                        <span className="text-secondary">with</span>
                        <Badge intent="purple" label="Authentication add-on" />
                      </h4>
                    </>
                  )}
                  {customTableHeader === 'Pro with Administration add-on' && (
                    <>
                      <h4 className="inline-flex items-center gap-1 text-[0.8125rem] leading-6">
                        <span className="font-semibold text-newPurple-500">
                          Pro
                        </span>
                        <span className="text-secondary">with</span>
                        <Badge intent="purple" label="Administration add-on" />
                      </h4>
                    </>
                  )}
                  {customTableHeader === 'Pro with B2B SaaS add-on' && (
                    <>
                      <h4 className="inline-flex items-center gap-1 text-[0.8125rem] font-semibold leading-6">
                        <span className="text-newPurple-500">Pro</span>
                        <span className="text-secondary">with</span>
                        <Badge intent="purple" label="B2B SaaS add-on" />
                      </h4>
                    </>
                  )}
                </div>
              </th>
            )}
          </tr>
        </thead>

        <tbody>
          {Object.entries(data).map(([feature, planDetails], index) => (
            <tr
              className="text-[0.8125rem] leading-5 text-secondary [&_td:first-child]:text-left [&_td]:py-4 [&_td]:pr-4 [&_td]:text-center"
              key={index}
            >
              <td className="relative font-medium">
                <div className="flex items-center gap-3">
                  {feature}
                  {planDetails.titleTooltip && (
                    <InfoTip label={planDetails.titleTooltip} />
                  )}
                </div>
                <DottedBorderSide side="right" overhang={0} />
                <DottedBorderSide side="bottom" overhang={0} />
              </td>

              <td className="relative">
                <div
                  className={cx('flex items-center justify-center gap-2', {
                    'translate-x-3': planDetails.freePlan.toolTip
                  })}
                >
                  {planDetails.freePlan.value === true && (
                    <Checkmark color="gray" />
                  )}
                  {planDetails.freePlan.value !== true &&
                    planDetails.freePlan.value}
                  {planDetails.freePlan.toolTip && (
                    <InfoTip label={planDetails.freePlan.toolTip} />
                  )}
                </div>
                <DottedBorderSide side="right" overhang={0} />
                <DottedBorderSide side="bottom" overhang={0} />
              </td>

              <td className="relative">
                {planDetails.proPlan.value === true && (
                  <Checkmark color="blue" />
                )}
                {planDetails.proPlan.value !== true &&
                  planDetails.proPlan.value}
                {planDetails.proPlan.toolTip && (
                  <InfoTip label={planDetails.proPlan.toolTip} />
                )}
                {totalDataKeys !== 2 && (
                  <DottedBorderSide side="right" overhang={0} />
                )}
                <DottedBorderSide side="bottom" overhang={0} />
              </td>
              {totalDataKeys === 3 && (
                <td className="relative">
                  {planDetails.proPlanPlus?.value === true && (
                    <Checkmark color="purple" />
                  )}
                  {planDetails.proPlanPlus?.value !== true &&
                    planDetails.proPlanPlus?.value}
                  {planDetails.proPlanPlus?.toolTip && (
                    <InfoTip label={planDetails.proPlanPlus.toolTip} />
                  )}
                  <DottedBorderSide side="bottom" overhang={0} />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface CondensedPricingTableProps {
  data: Record<string, PlanData>;
  title: string;
  planType: 'freePlan' | 'proPlan' | 'proPlanPlus';
}

export function PricingTableCondensed({
  data,
  title,
  planType
}: CondensedPricingTableProps) {
  const planExists = Object.values(data).some(planData => planType in planData);

  if (!planExists) {
    return null;
  }

  return (
    <div className="bg-white px-8 lg:hidden">
      <table className="mx-auto w-full divide-y">
        <thead>
          <tr className="[&_th:first-child]:pl-0 [&_th:first-child]:text-left [&_th:last-child]:pr-0 [&_th]:w-1/2 [&_th]:py-6 [&_th]:pl-6 [&_th]:pr-6 [&_th]:text-center">
            <th
              scope="col"
              className="relative text-[1.125rem] font-medium leading-7"
            >
              <h4>{title}</h4>
            </th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(data).map(([feature, planDetails], index) => {
            const detail = planDetails[planType];

            if (!detail) {
              return null;
            }

            return (
              <tr
                className="text-[0.8125rem] leading-5 [&_td:first-child]:text-left [&_td]:py-4 [&_td]:text-center"
                key={index}
              >
                <td className="relative font-medium">
                  {feature}
                  <DottedBorderSide side="right" overhang={0} />
                  <DottedBorderSide side="bottom" overhang={0} />
                </td>
                <td className="relative">
                  {detail.value === true ? (
                    <Checkmark
                      color={
                        planType === 'freePlan'
                          ? 'gray'
                          : planType === 'proPlan'
                          ? 'blue'
                          : 'purple'
                      }
                    />
                  ) : (
                    <span>{detail.value}</span>
                  )}
                  <DottedBorderSide side="bottom" overhang={0} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
